'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { leadSchema, type LeadInput } from '@/lib/schemas/lead';
import { track } from '@/lib/analytics/ym';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { FadeUp } from '@/components/motion';

const BUDGETS: { value: NonNullable<LeadInput['budget']>; label: string }[] = [
  { value: '<500K', label: 'до 500К' },
  { value: '500K-1M', label: '500К – 1М' },
  { value: '1M-3M', label: '1М-3М' },
  { value: '3M+', label: 'от 3М' },
  { value: 'undefined', label: 'Ещё не определился' },
];

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-destructive">{message}</p>;
}

export function LeadForm() {
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);

  function fireStart() {
    if (started) return;
    setStarted(true);
    track.formStart('lead');
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<LeadInput>({
    resolver: zodResolver(leadSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      company: '',
      pain: '',
      contact: '',
    },
  });

  const onInvalid = (errs: typeof errors) => {
    track.formError('lead', Object.keys(errs));
  };

  const onSubmit = async (data: LeadInput) => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.status === 429) {
        toast.warning('Уже отправляли — ответим в течение 4 часов');
        return;
      }
      if (!res.ok) {
        toast.error('Что-то сломалось, напишите в Telegram');
        return;
      }
      toast.success('Приняли. Ответим в течение 4 часов в выбранный канал.');
      track.formSubmit({
        form: 'lead',
        budget: data.budget,
      });
      reset();
      setStarted(false);
    } catch {
      toast.error('Что-то сломалось, напишите в Telegram');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead"
      className="bg-background container mx-auto max-w-2xl px-4 md:px-6 lg:px-8 py-24 md:py-32"
    >
      <FadeUp>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Следующий шаг
        </p>
      </FadeUp>
      <FadeUp delay={0.1}>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
          Запросить мини-аудит за 2-3 дня
        </h2>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          Письменный документ на 2 страницы: что мы увидели в вашем процессе и
          что предлагаем. Бесплатно.
        </p>
      </FadeUp>

      <FadeUp delay={0.3}>
        <ol className="mt-8 space-y-3 text-sm md:text-base">
          <li className="flex gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
            >
              1
            </span>
            <span>
              Мы смотрим форму в течение 4 часов и пишем вам в Telegram или email.
            </span>
          </li>
          <li className="flex gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
            >
              2
            </span>
            <span>
              Назначаем 30-минутное знакомство — звонок с вопросами про ваш
              процесс, без питча. Готовиться не нужно.
            </span>
          </li>
          <li className="flex gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold"
            >
              3
            </span>
            <span>
              Через 2-3 дня вы получаете письменный мини-аудит со ссылкой на
              похожий кейс.
            </span>
          </li>
        </ol>
      </FadeUp>

      <FadeUp delay={0.4}>
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          onFocus={fireStart}
          className="mt-10 space-y-5"
          noValidate
        >
          <div className="grid gap-2">
            <Label htmlFor="lf-name">Имя</Label>
            <Input
              id="lf-name"
              placeholder="Как к вам обращаться?"
              autoComplete="name"
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            <FieldError message={errors.name?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-company">Компания</Label>
            <Input
              id="lf-company"
              placeholder="Например, «Завод X»"
              autoComplete="organization"
              aria-invalid={!!errors.company}
              {...register('company')}
            />
            <FieldError message={errors.company?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-budget">Бюджет на пилот</Label>
            <Controller
              control={control}
              name="budget"
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="lf-budget"
                    className="w-full"
                    aria-invalid={!!errors.budget}
                  >
                    <SelectValue placeholder="Выберите бюджет" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError message={errors.budget?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-pain">Что хотите автоматизировать</Label>
            <Textarea
              id="lf-pain"
              rows={4}
              placeholder="Например: «11 000 PDF, менеджеры тратят 5-10 мин на поиск»"
              aria-invalid={!!errors.pain}
              {...register('pain')}
            />
            <p className="text-xs text-muted-foreground">20-2000 символов</p>
            <FieldError message={errors.pain?.message} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lf-contact">Как связаться</Label>
            <Input
              id="lf-contact"
              placeholder="@username, name@company.ru или +7 999 000-00-00"
              aria-invalid={!!errors.contact}
              {...register('contact')}
            />
            <p className="text-xs text-muted-foreground">
              Telegram, email или телефон — отвечу в течение 4 часов
            </p>
            <FieldError message={errors.contact?.message} />
          </div>

          <div className="flex items-start gap-3">
            <input
              id="lf-consent"
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-foreground"
              aria-invalid={!!errors.consent}
              {...register('consent')}
            />
            <Label
              htmlFor="lf-consent"
              className="text-sm text-muted-foreground leading-snug"
            >
              <span>
                Согласен на обработку персональных данных по{' '}
                <Link
                  href="/privacy"
                  className="underline underline-offset-4 hover:text-foreground"
                >
                  152-ФЗ
                </Link>
              </span>
            </Label>
          </div>
          <FieldError message={errors.consent?.message} />

          <button
            type="submit"
            disabled={submitting}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'min-h-12 w-full text-base md:w-auto rounded-full',
            )}
          >
            {submitting ? 'Отправляем…' : 'Отправить заявку'}
          </button>
        </form>
      </FadeUp>
    </section>
  );
}
